
import React, { useEffect, useMemo, useState } from 'react';
import type { SmartColumn } from './smartTable.types';
import CustomForm from '../../containers/custom-form';
import TextField from '../../controls/fields/textFieldNew';
import Button from '../../controls/buttons/button';
import './SmartTable.scss';
import { ReactComponent as IconSearch } from "itd-common/assets/icons/search-icon.svg";
import DropdownButton from '../../controls/buttons/dropdownButton';

interface ColumnSelectorProps {
  allColumns: SmartColumn[];
  /** Selección actualmente aplicada en la tabla */
  selectedKeys: string[];
  /** Callback para aplicar cambios (se dispara al click en "Aplicar") */
  onChange: (keys: string[]) => void;
  /** (Opcional) claves por defecto al restablecer; si no se pasa, se limpia */
  defaultKeys?: string[];
}

/** Helpers */
const safeStr = (s?: string | null) => (s ?? '').trim();
const cmpES = (a?: string | null, b?: string | null) =>
  safeStr(a).localeCompare(safeStr(b), 'es', { sensitivity: 'base' });

/** Grupo interno con id estable */
type ColumnGroup = {
  id: string;           // category o '__UNGROUPED__'
  label: string;        // título visible del grupo
  items: SmartColumn[];
};

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  allColumns,
  selectedKeys,
  onChange,
  defaultKeys,
}) => {
  /** Búsqueda */
  const [query, setQuery] = useState({ search: '' });

  /** Estado local de selección (draft), independiente de selectedKeys hasta "Aplicar" */
  const [draftSelectedKeys, setDraftSelectedKeys] = useState<string[]>(selectedKeys);

  /** Colapsables por grupo */
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  /** Sincronizar draft cuando selectedKeys cambie afuera (ej. al abrir panel) */
  useEffect(() => {
    setDraftSelectedKeys(selectedKeys);
  }, [selectedKeys]);

  /** 1) Filtrado por label y categoría */
  const filteredColumns = useMemo(() => {
    const q = safeStr(query.search).toLowerCase();

    const visibleColumns = allColumns.filter(
      (c) => !c.def?.hidden && safeStr(c.key) !== '' && safeStr(c.label) !== ''
    );

    if (!q) return visibleColumns;

    return visibleColumns.filter((c) => {
      const labelMatch = safeStr(c.label).toLowerCase().includes(q);
      const categoryMatch = safeStr(c.def?.category).toLowerCase().includes(q);
      return labelMatch || categoryMatch; // si preferís sólo label: return labelMatch;
    });
  }, [query, allColumns]);

  /** 2) Agrupado por categoría y orden por cantidad (desc), luego por label */
  const groupedColumns = useMemo<ColumnGroup[]>(() => {
    const map = new Map<string, SmartColumn[]>();

    for (const col of filteredColumns) {
      const categoryKey = safeStr(col.def?.category) || '__UNGROUPED__';
      const list = map.get(categoryKey);
      if (list) list.push(col);
      else map.set(categoryKey, [col]);
    }

    const groups: ColumnGroup[] = Array.from(map.entries()).map(([key, items]) => ({
      id: key,
      label: key === '__UNGROUPED__' ? '' : key,
      items: items.slice().sort((a, b) => cmpES(a.label, b.label)),
    }));

    // Orden: más columnas primero; empate por label
    groups.sort((a, b) => {
      const diff = b.items.length - a.items.length;
      if (diff !== 0) return diff;
      return cmpES(a.label, b.label);
    });

    return groups;
  }, [filteredColumns]);

  /** 3) Toggle de colapso por grupo */
  const toggleCollapse = (groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  /** 4) Toggle individual por checkbox (sólo afecta el draft) */
  const toggleColumnDraft = (key: string) => {
    setDraftSelectedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  /** 5) Limpiar búsqueda (arriba) */
  const clearSearch = () => setQuery({ search: '' });

  /** 6) Restablecer (abajo): limpiar o volver a defaultKeys */
  const handleReset = () => {
    let values: string[]= [];
    if (defaultKeys && defaultKeys.length > 0) {
      values=defaultKeys;
    } 
    onChange(values);
    setDraftSelectedKeys(values);
    //  limpiar la búsqueda:
    setQuery({ search: '' });
  };

  /** 7) Aplicar (abajo): enviar el draft al padre */
  const handleApply = () => {
    onChange(draftSelectedKeys);
  };

  return (
    <div className="personalize-list-columns">
      {/* Filtro de búsqueda + limpiar búsqueda */}
      <div className="st-filter__row">
        <CustomForm model={[query, setQuery]}>
          <div className="col-md-12 d-flex gap-1">
            <TextField
              attr="search"
              placeholder="Buscar columna"
              label=""
              className="mt-0 st-input"
              prefix={<IconSearch />}
            />
          </div>
        </CustomForm>
      </div>

      {/* Listado agrupado con colapsables */}
      <div className="st-columns-grouped">
        {groupedColumns.map((group) => {
          const isCollapsed = collapsedGroups.has(group.id);
          const showHeader = group.label !== '';

          return (
            <div key={group.id} className={`st-column-group ${isCollapsed ? 'is-collapsed' : ''}`}>
              {showHeader && (
                <DropdownButton onClick={() => toggleCollapse(group.id)} label={group.label} isCollapsed={isCollapsed} key={group.id} />
              )}

              <div
                id={`group-items-${group.id}`}
                className="st-column-group__items"
                style={{ display: isCollapsed ? 'none' : 'grid' }}
              >
                {group.items.map((c) => (
                  <label key={c.key} className="st-column-item">
                    <input
                      type="checkbox"
                      checked={draftSelectedKeys.includes(c.key)}
                      onChange={() => toggleColumnDraft(c.key)}
                    />
                    <span className="st-column-item__label">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        {filteredColumns.length === 0 && (
          <div className="st-empty">Sin columnas</div>
        )}
      </div>

      <div className="st-actions">
        <button className="bottonRestore" onClick={handleReset}><span className="restoreText"> Restablecer </span></button>
        <button className="bottonApply" onClick={handleApply}><span className="applyText">Aplicar</span></button>
      </div>
    </div>
  );
};

export default ColumnSelector;